import { Navigate, useNavigate, useSearchParams } from "@solidjs/router";
import { onMount, createSignal, createEffect } from "solid-js";
import { isServer } from "solid-js/web";

import { useRIASApp } from "~/_context/UserProvider";
import { generateCodeVerifier, generateCodeChallenge } from "~/lib";

import { COGNITO_CLIENTID, COGNITO_USERPOOL } from "~/_server/cognito";

import { updateDB } from "~/_server";
import { ADMIN } from "~/_server/constants";

const oauthHost = `https://${COGNITO_USERPOOL.replace( "_", "" )}.auth.eu-west-2.amazoncognito.com`;
const loginHost = 'https://login.rias-regs.co.uk';

export default () => {
	"use client";

	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const app = useRIASApp();

	const [callbackUrl, setCallbackUrl] = createSignal<string>( '' );
	const [loggedIn, setLoggedIn] = createSignal( false );
	const [ready, setReady] = createSignal( false );

	const loginRedirect = async () => {
		const genCodeVerifier = generateCodeVerifier();
		const codeChallenge = await generateCodeChallenge( genCodeVerifier );

		sessionStorage.setItem( "code_verifier", genCodeVerifier );

		const loginUrl = `${loginHost}/login` +
			`?response_type=code` +
			`&client_id=${COGNITO_CLIENTID}` +
			`&redirect_uri=${encodeURIComponent( callbackUrl() )}` +
			`&scope=email+openid+phone+profile` +
			`&code_challenge_method=S256` +
			`&code_challenge=${codeChallenge}`;

		window.location.href = loginUrl;
	}

	onMount( () => {
		setReady( true );
		setCallbackUrl( () => `${window.location.origin}/login` )

		if ( searchParams.next ) {
			sessionStorage.setItem( "postLoginRedirect", searchParams.next as string );
		}
	} );

	createEffect( () => {
		if ( loggedIn() ) {
			const path = decodeURIComponent( sessionStorage.getItem( "postLoginRedirect" ) ?? "/dashboard" );

			if ( !isServer ) {
				sessionStorage.removeItem( "postLoginRedirect" );
			}


			navigate( path, { replace: true } );
		}
	} );

	createEffect( async () => {
		if ( !isServer && ready() ) {
			const params = new URLSearchParams( window.location.search );
			const code = params.get( "code" );
			const codeVerifier = sessionStorage.getItem( "code_verifier" );

			if ( !codeVerifier || !code ) {
				await loginRedirect();
			} else {
				const body = new URLSearchParams( {
					grant_type: "authorization_code",
					client_id: COGNITO_CLIENTID,
					code: code ?? "",
					redirect_uri: callbackUrl(),
					code_verifier: codeVerifier!.toString()
				} );

				const res = await fetch( `${oauthHost}/oauth2/token`, {
					method: "POST",
					headers: { "Content-Type": "application/x-www-form-urlencoded" },
					body,
				} );

				const data = await res.json();
				const { access_token: accessToken, id_token: idToken, refresh_token: refreshToken } = { ...data };

				if ( accessToken ) {
					localStorage.setItem( "accessToken", accessToken );
					localStorage.setItem( "idToken", idToken );

					await fetch( '/api/public/auth-cookie', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify( { accessToken, idToken, refreshToken } )
					} );

					setLoggedIn( true );
				} else {
					await loginRedirect();
				}
			}
		}
	} );

	app.refetchUser();

	return loggedIn() ? <Navigate href={ app.userIs( ADMIN ) ? "/dashboard/admin": "/dashboard" } /> : <p class="py-[45vh] text-center text-2xl">Logging you in...</p>;
}
